<script setup>
import { ref, reactive } from 'vue';
import { useRouter } from 'vue-router';
import { useLayoutComponents } from '@/composables/layout.js';
import { useIcons } from '@/composables/icons.js';
import { useToast } from '@/composables/use-toast.js';
import axios from '@/plugins/axios';

// Composables
const router = useRouter();
const { TextInput, Divider, PrimaryButton, ButtonLoading, Toast } = useLayoutComponents();
const { Envelope, Lock } = useIcons();
const { addToast } = useToast();

// Reactive state
const doNotMatch = ref(false);
const success = ref(false);
const error = ref(false);
const email = ref('');
const loading = ref(false);

const resetAccount = reactive({
  newPassword: '',
  confirmPassword: '',
});

const rules = {
  resetAccount: {
    newPassword: { 
      required: true, 
      minLength: 4, 
      maxLength: 50 
    },
    confirmPassword: { 
      required: true, 
      minLength: 4, 
      maxLength: 50,
    },
    email: {
      required: true,
      email: true,
    },
  },
};

const finishReset = async () => {
  if (resetAccount.newPassword !== resetAccount.confirmPassword) {
    doNotMatch.value = true;
    error.value = true;
    return;
  }

  if (rules.resetAccount.newPassword.minLength > resetAccount.newPassword.length) {
    error.value = true;
    return;
  }

  if (rules.resetAccount.newPassword.maxLength < resetAccount.newPassword.length) {
    error.value = true;
    return;
  }

  if (!resetAccount.confirmPassword) {
    error.value = true;
    return;
  }

  if (!email.value) {
    error.value = true;
    return;
  }

  const token = window.location.href.split('?token=')[1];
  localStorage.setItem('AuthToken', token);

  loading.value = true;

  try {
  const response = await axios.post('api/login/reset-password', { 
    email: email.value, 
    token: token, 
    new_password: resetAccount.newPassword, 
    confirm_password: resetAccount.confirmPassword 
  });

  if (response.data.statusCode === 200) {
    success.value = 'OK';
    localStorage.removeItem('AuthToken');
    addToast('Password reset successfully', 'success');
    router.push('/login');
  } else {
    addToast('Failed to reset password', 'error');
  }
  } catch (error) {
    console.log(error);
    addToast(error.response?.data?.message || 'Failed to reset password', 'error');
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <Navbar :navLight="'nav-light'" />
  <Toast />
  <div class="reset-password">
    <div class="reset-password-card">
      <!-- Header -->
      <div class="reset-password-card__header">
        <img src="/favicon.ico" alt="logo" class="reset-password-card__header-logo" />
        <h2 class="reset-password-card__header-title">Reset Password</h2>
        <span class="reset-password-card__header-subtitle">
          Reset your account password
        </span>
      </div>
      
      <!-- Body -->
      <div class="reset-password-card__body">
        <form @submit.prevent="finishReset" class="reset-password-card__body-form">
          <!-- Email input -->
          <div class="reset-password-card__body-form-group">
            <TextInput v-model="email" type="email" id="email" placeholder="email address" required>
              <template #icon>
                <Envelope class="w-5 h-5" />
              </template>
            </TextInput>
          </div>

          <!-- New password input -->
          <div class="reset-password-card__body-form-group">
          <TextInput v-model="resetAccount.newPassword" type="password" id="newPassword" placeholder="new password" required>
            <template #icon>
              <Lock class="w-5 h-5" />
            </template>
          </TextInput>     
        </div>

          <!-- Confirm password input -->
          <div class="reset-password-card__body-form-group">
          <TextInput v-model="resetAccount.confirmPassword" type="password" id="confirmPassword" placeholder="confirm password" required>
            <template #icon>
              <Lock class="w-5 h-5" />
            </template>
          </TextInput>     
        </div>

          <!-- reset-password button -->
          <div class="text-center">
            <PrimaryButton class="reset-password-card__body-form-button" @click="finishReset" :disabled="loading">
              <ButtonLoading v-if="loading" size="sm" text="Resetting password..." />
              <template v-else>Reset password</template>
            </PrimaryButton>
          </div>
        </form>

        <div class="text-center">
          <router-link to="/login" @click="showLogin" class="text-sm text-gray-400 hover:text-indigo-300 font-semibold">
            Back to login
          </router-link>
        </div>

        <!-- Social reset-password section -->
        <Divider text="OR" class="hidden" />
        <div class="social-reset-password-buttons">
          <!-- Apple reset-password -->
          <button class="social-button">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24">
              <path fill="#FFFFFF"
                d="M17.537 12.625a4.421 4.421 0 0 0 2.684 4.047 10.96 10.96 0 0 1-1.384 2.845c-.834 1.218-1.7 2.432-3.062 2.457-1.34.025-1.77-.794-3.3-.794-1.531 0-2.01.769-3.275.82-1.316.049-2.317-1.318-3.158-2.532-1.72-2.484-3.032-7.017-1.27-10.077A4.9 4.9 0 0 1 8.91 6.884c1.292-.025 2.51.869 3.3.869.789 0 2.27-1.075 3.828-.917a4.67 4.67 0 0 1 3.66 1.984 4.524 4.524 0 0 0-2.16 3.805m-2.52-7.432A4.4 4.4 0 0 0 16.06 2a4.482 4.482 0 0 0-2.945 1.516 4.185 4.185 0 0 0-1.061 3.093 3.708 3.708 0 0 0 2.967-1.416Z" />
            </svg>
          </button>
          
          <!-- Google reset-password -->
          <button class="social-button">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24">
              <path fill="#FFFFFF" fill-rule="evenodd"
                d="M12.037 21.998a10.313 10.313 0 0 1-7.168-3.049 9.888 9.888 0 0 1-2.868-7.118 9.947 9.947 0 0 1 3.064-6.949A10.37 10.37 0 0 1 12.212 2h.176a9.935 9.935 0 0 1 6.614 2.564L16.457 6.88a6.187 6.187 0 0 0-4.131-1.566 6.9 6.9 0 0 0-4.794 1.913 6.618 6.618 0 0 0-2.045 4.657 6.608 6.608 0 0 0 1.882 4.723 6.891 6.891 0 0 0 4.725 2.07h.143c1.41.072 2.8-.354 3.917-1.2a5.77 5.77 0 0 0 2.172-3.41l.043-.117H12.22v-3.41h9.678c.075.617.109 1.238.1 1.859-.099 5.741-4.017 9.6-9.746 9.6l-.215-.002Z"
                clip-rule="evenodd" />
            </svg>
          </button>
          
          <!-- Twitter/X reset-password -->
          <button class="social-button">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24">
              <path fill="#FFFFFF"
                d="M13.795 10.533 20.68 2h-3.073l-5.255 6.517L7.69 2H1l7.806 10.91L1.47 22h3.074l5.705-7.07L15.31 22H22l-8.205-11.467Zm-2.38 2.95L9.97 11.464 4.36 3.627h2.31l4.528 6.317 1.443 2.02 6.018 8.409h-2.31l-4.934-6.89Z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.reset-password {
  @apply flex justify-center items-center h-[100dvh] w-screen px-4;
  background-image: linear-gradient(to bottom, transparent, #1e293b), url('@/assets/images/bg/bg3.webp');
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

.reset-password-card {
  @apply w-full max-w-sm rounded-2xl p-4 shadow-lg;
  background-color: rgba(24, 24, 27, 0.8);
  backdrop-filter: blur(8px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3), 0 4px 6px rgba(0, 0, 0, 0.2);
  border: 1px solid transparent;
  background-clip: padding-box;
}

.reset-password-card__header {
  @apply mb-4 flex flex-col items-center justify-center gap-2;
}

.reset-password-card__header-title {
  @apply text-2xl font-semibold text-white;
}

.reset-password-card__header-subtitle {
  @apply text-sm text-gray-400;
}

.reset-password-card__header-logo {
  @apply w-12 h-12;
}

.reset-password-card__body {
  @apply space-y-4;
}

.reset-password-card__body-form {
  @apply flex flex-col gap-2;
}

.reset-password-card__body-form-group {
  @apply flex flex-col gap-2;
}

.reset-password-card__body-form-button {
  @apply w-full shadow-lg;
}

.reset-password-card__footer {
  @apply mt-6 flex justify-between;
}

.social-reset-password-buttons {
  @apply hidden justify-center gap-4;
}

.social-button {
  @apply flex items-center justify-center gap-2 w-full p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 transition-colors shadow-md;
}
</style>